import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * These tables carry 7+ columns of operational data and are read on phones as
 * well as desktops. Three things make that survivable, and none of them were
 * here:
 *
 *  - the header stays put while a 200-row list scrolls, so you always know
 *    which column you are reading;
 *  - the first column stays put while you scroll sideways, so a row keeps its
 *    identity — at 375px only two of seven columns fit, and without this you
 *    lose track of whose row you are on;
 *  - a fade on the right edge says there is more to the side, which a plain
 *    clipped table does not.
 */
const Table = ({ className, stickyFirstColumn = true, compact = false, ref, ...props }) => (
  <div className="relative w-full">
    {/* A sticky header only holds if its scroll container has a bounded
        height — otherwise the container scrolls away with the page and the
        header goes with it. The cap is applied from md up, where lists run
        long and a nested scroll region is natural with a mouse; on a phone
        the page keeps scrolling normally. */}
    <div className="w-full overflow-auto md:max-h-[calc(100vh-17rem)]">
      <table
        ref={ref}
        className={cn(
          'w-full caption-bottom text-sm',
          stickyFirstColumn && 'table-sticky-col',
          // `compact`: 8 px de marge par côté au lieu de 12, pour qu'un tableau
          // de douze colonnes tienne sans défiler à 1440 px.
          compact && '[&_th]:px-2 [&_td]:px-2',
          className,
        )}
        {...props}
      />
    </div>
    {/* Scroll affordance. Sits above the scroll area, ignores pointer events. */}
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-card to-transparent md:hidden"
    />
  </div>
);
Table.displayName = 'Table';

const TableHeader = ({ className, ref, ...props }) => (
  <thead
    ref={ref}
    className={cn(
      'sticky top-0 z-20 bg-card [&_tr]:border-b',
      className,
    )}
    {...props}
  />
);
TableHeader.displayName = 'TableHeader';

const TableBody = ({ className, ref, ...props }) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
);
TableBody.displayName = 'TableBody';

const TableFooter = ({ className, ref, ...props }) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-muted/50 font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
);
TableFooter.displayName = 'TableFooter';

const TableRow = ({ className, ref, ...props }) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-border/60 transition-colors hover:bg-muted/60 data-[state=selected]:bg-primary/10',
      className,
    )}
    {...props}
  />
);
TableRow.displayName = 'TableRow';

const TableHead = ({ className, ref, ...props }) => (
  <th
    ref={ref}
    className={cn(
      // Column labels read as labels, not as data: smaller, tracked out, and
      // never wrapping so the header stays one line deep.
      'h-9 whitespace-nowrap px-3 text-left align-middle text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className,
    )}
    {...props}
  />
);
TableHead.displayName = 'TableHead';

const TableCell = ({ className, ref, ...props }) => (
  <td
    ref={ref}
    className={cn(
      'px-3 py-2.5 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
      className,
    )}
    {...props}
  />
);
TableCell.displayName = 'TableCell';

const TableCaption = ({ className, ref, ...props }) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
);
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
